import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import styled from 'styled-components';

import {
  H3,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Link,
  SearchInput,
  Filter,
} from 'next/components/ui';
import useFilter from 'next/hooks/use-filter';
import Loader from 'components/Common/Loader';
import NoData from 'assets/images/no-data.svg';
import Pagination from 'components/Common/UI/Pagination/Pagination';
import { getAnalytics, remove } from 'actions/analytics-v2';
import { selectUserActivityDetails } from 'selectors/user-activities';
import { getCacheKey } from 'utils/caching';

import {
  ProfilePanel,
  TableWrapper,
  PanelHeader,
  LoaderWrapper,
  NoResultsMessage,
  Footer,
  PaginationInformation,
  NoResultsMessageEmphasis,
  PanelTitle,
  Subtitle,
} from 'components/Users/Profile/styled';
import { DEFAULT_PAGE_SIZE } from 'components/Users/Profile/constants';
import NoResults from 'assets/images/no-results.svg';

import {
  ActivitiesTable,
  ActionIcon,
  Action,
  ActionLabel,
  Filters,
  ActivitiesRow,
} from './styled';

const ACTION_LABELS = {
  started: 'Started',
  completed: 'Completed',
  skipped: 'Skipped',
  triggered: 'Triggered',
};

const ACTION_ICONS = {
  started: 'adjust',
  completed: 'check-circle',
  skipped: 'ban',
  triggered: 'circle',
};

const ACTIVITY_LABELS = {
  flow: 'Flow',
  checklist: 'Checklist',
  nps: 'NPS',
  goal: 'Goal',
  application: 'Application Event',
  builder: 'Track Events (Builder)',
};

const ActivityTime = styled.span``;

export const Activities = ({
  query,
  activities,
  pageSize = DEFAULT_PAGE_SIZE,
  onLoad,
  onUnload,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const { filters, filterFunction, handleFilterChange, handleFilterClear } =
    useFilter({
      actions: {
        name: 'Actions',
        options: Object.entries(ACTION_LABELS).reduce(
          (acc, [action, label]) => {
            acc[action] = {
              label: (
                <ActionLabel>
                  <Action>{label}</Action>
                  <ActionIcon $action={action} icon={ACTION_ICONS[action]} />
                </ActionLabel>
              ),
              enabled: false,
              filterFn: ({ action: a }) => a === action,
            };
            return acc;
          },
          {}
        ),
      },
      activities: {
        name: 'Activities',
        options: Object.entries(ACTIVITY_LABELS).reduce(
          (acc, [activity, label]) => {
            acc[activity] = {
              label,
              enabled: false,
              filterFn: ({ activity: a }) => a === activity,
            };
            return acc;
          },
          {}
        ),
      },
    });

  useEffect(() => {
    onLoad?.({ query });
    return () => {
      onUnload?.(getCacheKey(query));
    };
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [filters, query]);

  const filteredActivities = activities
    ?.filter(({ displayName }) =>
      `${displayName}`.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(filterFunction);

  const activitiesSlice = filteredActivities?.slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize
  );

  const isLoading = !activities;
  const hasNoResults = activities?.length === 0;
  const hasNoMatches = !hasNoResults && filteredActivities?.length === 0;

  return (
    <ProfilePanel>
      <PanelHeader>
        <PanelTitle>
          <H3>Activities</H3>
          <Subtitle>1,000 most recent activities timeline</Subtitle>
        </PanelTitle>
        <Filters>
          <SearchInput
            placeholder="Search by name"
            value={searchTerm}
            onChange={({ target: { value } }) => {
              setSearchTerm(value);
            }}
          />
          <Filter
            filters={filters}
            onChange={handleFilterChange}
            onClear={handleFilterClear}
          />
        </Filters>
      </PanelHeader>
      <TableWrapper>
        <ActivitiesTable>
          <Thead>
            <Tr>
              <Th>Action</Th>
              <Th>Activity</Th>
              <Th>Name</Th>
              <Th>Time</Th>
            </Tr>
          </Thead>
          <Tbody>
            {activitiesSlice?.map(
              ({ action, activity, displayName, href, timestamp }) => {
                return (
                  <ActivitiesRow>
                    <Td>
                      <ActionLabel>
                        <ActionIcon
                          $action={action}
                          icon={ACTION_ICONS[action]}
                        />
                        <Action>{ACTION_LABELS[action]}</Action>
                      </ActionLabel>
                    </Td>
                    <Td>{ACTIVITY_LABELS[activity]}</Td>
                    <Td>
                      <Link title={displayName} to={href} target="_blank">
                        {displayName}
                      </Link>
                    </Td>
                    <Td>
                      <ActivityTime title={moment(timestamp).format('lll')}>
                        {moment(timestamp).fromNow()}
                      </ActivityTime>
                    </Td>
                  </ActivitiesRow>
                );
              }
            )}
          </Tbody>
        </ActivitiesTable>
        {isLoading && (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        )}
        {hasNoResults && (
          <NoResultsMessage>
            <img src={NoData} alt="No data found" />
            We don’t have data for you yet
          </NoResultsMessage>
        )}
        {hasNoMatches && (
          <NoResultsMessage>
            <img src={NoResults} alt="No data found" />
            <NoResultsMessageEmphasis>
              No results found
            </NoResultsMessageEmphasis>
            Try adjusting your filters for results
          </NoResultsMessage>
        )}
      </TableWrapper>
      <Footer>
        {filteredActivities?.length > 0 && (
          <>
            <Pagination
              pageCount={Math.ceil(filteredActivities.length / pageSize)}
              currentIndex={currentPage}
              onChange={setCurrentPage}
            />

            <PaginationInformation
              currentIndex={currentPage}
              pageSize={pageSize}
              total={filteredActivities?.length}
            />
          </>
        )}
      </Footer>
    </ProfilePanel>
  );
};

Activities.propTypes = {
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      action: PropTypes.oneOf(Object.keys(ACTION_ICONS)),
      activity: PropTypes.oneOf(ACTIVITY_LABELS),
      displayName: PropTypes.string,
      href: PropTypes.string,
      timestamp: PropTypes.string,
    })
  ),
  query: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.string),
    dimensions: PropTypes.arrayOf(PropTypes.string),
    conditions: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ])
    ),
    order_by: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    limit: PropTypes.number,
  }),
  pageSize: PropTypes.number,
  onLoad: PropTypes.func,
  onUnload: PropTypes.func,
};

const mapStateToProps = (state, { userId, endTime: end_time }) => {
  const query = {
    columns: [
      'timestamp',
      'name',
      'flow_id',
      'checklist_id',
      'goal_id',
      'a:score',
      'a:feedback',
    ],
    dimension: ['user_id'],
    conditions: [
      ['user_id', '==', userId],
      [
        'or',
        [
          'name',
          'in',
          [
            'appcues:flow_started',
            'appcues:flow_completed',
            'appcues:checklist_shown',
            'appcues:checklist_completed',
            'appcues:checklist_skipped',
            'appcues:nps_score',
            'appcues:nps_feedback',
            'appcues:goal_reached',
          ],
        ],
        ['name', 'like', 'appcues_custom:%'],
        ['name', 'not like', 'appcues:%'],
      ],
    ],
    order_by: [['timestamp', 'desc']],
    start_time: moment(end_time).subtract(30, 'days'),
    end_time,
    limit: 1000,
  };

  return {
    query,
    activities: selectUserActivityDetails(state, query),
  };
};

const mapDispatchToProps = {
  onLoad: getAnalytics,
  onUnload: remove,
};

const ConnectedActivities = connect(
  mapStateToProps,
  mapDispatchToProps
)(Activities);

ConnectedActivities.propTypes = {
  userId: PropTypes.string,
  endTime: PropTypes.number,
};

export default ConnectedActivities;
